import { useTranslation } from "react-i18next";
import { ConfirmModal } from "../ConfirmModal/ConfirmModal";

import { FC, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { SelectedTracker } from "src/pages/Trackers/CreateTrackers/components/SelectSearchesSection/components";
import { useModal, useTrackerLanguage, useTrackerLocation } from "src/hooks";
import { useSelector } from "react-redux";
import { selectSearchesByTrackerId, selectUser } from "src/store/selectors";
import { TrendAlertItems } from "./components/TrendAlertItems/TrendAlertItems";
import { AddVolumeTrendAlert } from "./components/AddVolumeTrendAlert/AddVolumeTrendAlert";
import { AddNewDataAlert } from "./components/AddNewDataAlert/AddNewDataAlert";
import { TrendAlertConfirmation } from "./components/TrendAlertConfirmation/TrendAlertConfirmation";
import styles from "./TrendAlertModal.module.scss";
import {
  createTrendAlert,
  getCurrentVolume,
  updateTrendAlert,
  setCurrentVolumeFromAlert,
} from "src/store/trendAlerts/trendAlertsSlice";
import { useAppDispatch } from "src/store";
import {
  selectCurrentVolumeByTrackerId,
  selectHasDuplicateAlert,
  selectIsLoadingCurrentVolume,
  selectTrendAlertsByTrackerId,
} from "src/store/trendAlerts/trendAlertsSelector";
import { getTrackersByIds } from "src/store/trackers/trackersApi";
import { UnsavedChangesModal } from "../UnsavedChangesModal/UnsavedChangesModal";
import { showToastNotification } from "src/utils";
import { getTrackerName } from "./utils";
import { Tabs } from "src/components";

type Props = {
  trackerId: Tracker.Data["id"];
  currentTab?: string;
};

export const TrendAlertModal: FC<Props> = ({
  trackerId,
  currentTab = "volume",
}) => {
  const [step, setStep] = useState(1);
  const [percentage, setPercentage] = useState("");
  const [isModified, setIsModified] = useState(false);
  const [initialValue, setInitialValue] = useState<string | number>("");
  const [createdAlert, setCreatedAlert] = useState<TrendAlert.Data | null>(
    null,
  );
  const [editMode, setEditMode] = useState(false);
  const [selectedAlert, setSelectedAlert] =
    useState<TrendAlert.CreationData | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [tracker, setTracker] = useState<Tracker.Data | null>(null);
  const [targetVolume, setTargetVolume] = useState<number | null>(null);
  const [type, setType] = useState<TrendAlert.Type>(
    currentTab === "volume" ? "consistent" : "suggestions",
  );
  const [absoluteNumber, setAbsoluteNumber] = useState<number | null>(null);
  const [activeTab, setActiveTab] = useState<string>(currentTab);
  const [minVolume, setMinVolume] = useState<number | null>(null);
  const [initialMinVolume, setInitialMinVolume] = useState<number | null>(null);

  const currentUser = useSelector(selectUser);

  const [selectedUserId, setSelectedUserId] = useState<string>(currentUser.id);

  const dispatch = useAppDispatch();
  const { t } = useTranslation();
  const { closeModal, setModal } = useModal();

  const currentVolume = useSelector(selectCurrentVolumeByTrackerId(trackerId));
  const isLoadingCurrentVolume = useSelector(
    selectIsLoadingCurrentVolume(trackerId),
  );
  const existingAlerts = useSelector((state: Store.RootState) =>
    selectTrendAlertsByTrackerId(state, trackerId),
  );
  const hasAttemptedVolumeFetch = useRef(false);

  const resetAlertState = () => {
    setStep(2);
    setPercentage("");
    setEditMode(false);
    setSelectedAlert(null);
    setCreatedAlert(null);
    setInitialValue("");
    if (activeTab === "volume") {
      setType(currentVolume ? "consistent" : "oneTime");
      setTargetVolume(null);
    } else {
      setType("suggestions");
      setMinVolume(null);
      setInitialMinVolume(null);
    }
  };

  const handleError = useCallback(
    (key: string, error?: unknown) => {
      console.error(t(key), error);
      showToastNotification({ type: "error", text: t(key) });
    },
    [t],
  );

  useEffect(() => {
    const fetchTrackerData = async () => {
      setIsLoading(true);
      try {
        const trackers = await getTrackersByIds([trackerId]);
        setTracker(trackers[0] || null);
      } catch (error) {
        handleError("component.modal.trend_alerts.error_fetching", error);
      } finally {
        setIsLoading(false);
      }
    };
    fetchTrackerData();
  }, [handleError, trackerId]);

  useEffect(() => {
    hasAttemptedVolumeFetch.current = false;
  }, [trackerId, activeTab]);

  useEffect(() => {
    const fetchVolume = async () => {
      if (activeTab !== "volume") return;

      if (currentVolume !== null) {
        hasAttemptedVolumeFetch.current = true;
        return;
      }

      if (hasAttemptedVolumeFetch.current) return;

      const volumeAlerts = existingAlerts.filter(
        (alert) =>
          (alert.type === "consistent" || alert.type === "oneTime") &&
          alert.currentVolume,
      );

      if (volumeAlerts.length) {
        const volume = volumeAlerts[0]?.currentVolume;

        if (volume) {
          dispatch(setCurrentVolumeFromAlert({ trackerId, volume }));
          hasAttemptedVolumeFetch.current = true;
          return;
        }
      }

      const hasVolumeAlerts = existingAlerts.some(
        (alert) =>
          (alert.type === "consistent" || alert.type === "oneTime") &&
          alert.currentVolume,
      );

      if (!hasVolumeAlerts) {
        hasAttemptedVolumeFetch.current = true;
        await dispatch(getCurrentVolume(trackerId));
      }
    };

    fetchVolume();
  }, [dispatch, trackerId, activeTab, currentVolume, existingAlerts]);

  const searches = useSelector((state: Store.RootState) =>
    selectSearchesByTrackerId(state, trackerId),
  );

  const trackerLocation = useTrackerLocation(searches);

  const trackerLanguage = useTrackerLanguage(searches);

  const disableCreateNewDataAlert = useMemo(() => {
    return (
      activeTab === "newData" &&
      [1, 2].includes(step) &&
      existingAlerts.some((alert) => alert.type === "suggestions")
    );
  }, [activeTab, existingAlerts, step]);

  const formattedTracker = useMemo<Nullable<Tracker.CreationData>>(() => {
    if (!tracker) return null;

    return {
      ...tracker,
      locationId: trackerLocation.id,
      languageId: trackerLanguage.id,
    };
  }, [tracker, trackerLanguage.id, trackerLocation.id]);

  const hasDuplicate = useSelector((state: Store.RootState) => {
    if (!tracker) return false;
    return selectHasDuplicateAlert(currentUser.id, tracker.id, type, {
      percentage: type === "consistent" ? percentage : undefined,
      targetVolume:
        type === "oneTime" ? absoluteNumber ?? undefined : undefined,
      minVolume: type === "suggestions" ? minVolume ?? undefined : undefined,
      excludeId: editMode ? selectedAlert?.id : undefined,
    })(state);
  });

  const createNewTrendAlert = async () => {
    try {
      if (!tracker) return;

      if (hasDuplicate) {
        handleError("component.modal.trend_alerts.error_duplicate");
        return;
      }

      const trackerName = getTrackerName(tracker.name);

      const result = await dispatch(
        createTrendAlert({
          companyId: tracker.companyId,
          authorId: currentUser.id,
          trackerId,
          trackerName,
          type,
          currentVolume: currentVolume || 0,
          baseVolume: currentVolume || 0,
          percentage: type === "consistent" ? percentage : "",
          absoluteNumber: type === "oneTime" ? absoluteNumber : null,
          minVolume: type === "suggestions" ? minVolume : null,
        }),
      ).unwrap();

      setCreatedAlert(result);
      setStep(3);
    } catch (error) {
      handleError("component.modal.trend_alerts.error_creating", error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleEditAlert = async (alert: TrendAlert.CreationData) => {
    try {
      if (!alert) return;
      setSelectedAlert(alert);
      setEditMode(true);
      setStep(2);
      setPercentage(alert.percentage || "");
      setInitialValue(
        alert.type === "consistent" ? alert.percentage : alert.targetVolume,
      );
      setType(alert.type || "consistent");
      setTargetVolume(alert.targetVolume);
      setAbsoluteNumber(alert.targetVolume);
      setMinVolume(alert.minVolume);
      setInitialMinVolume(alert.minVolume);
    } catch (error) {
      handleError("component.modal.trend_alerts.error_editing", error);
    }
  };

  const updateSelectedTrendAlert = async () => {
    if (!tracker || !selectedAlert) return;
    if (hasDuplicate) {
      handleError("component.modal.trend_alerts.error_duplicate");
      setIsLoading(false);
      return;
    }

    const trackerName = getTrackerName(tracker.name);

    try {
      const response = await dispatch(
        updateTrendAlert({
          id: selectedAlert.id,
          trackerId: tracker.id,
          trackerName,
          companyId: tracker.companyId,
          authorId: currentUser.id,
          type,
          baseVolume: currentVolume || 0,
          percentage: type === "consistent" ? percentage : "",
          absoluteNumber: type === "oneTime" ? absoluteNumber : null,
          minVolume: type === "suggestions" ? minVolume : null,
        }),
      ).unwrap();

      if (!response) {
        handleError("component.modal.trend_alerts.error_updating");
        return;
      }

      const updatedAlert: TrendAlert.Data = response;

      setCreatedAlert(updatedAlert);
      setStep(3);
    } catch (error) {
      handleError("component.modal.trend_alerts.error_updating", error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSave = () => {
    setIsLoading(true);
    if (editMode) {
      updateSelectedTrendAlert();
    } else {
      createNewTrendAlert();
    }
  };

  const getModalConfig = () => {
    switch (step) {
      case 1:
        return {
          content: (
            <>
              <div className={styles.buttonsWrapper}>
                <Tabs
                  className={styles.tabs}
                  activeTab={activeTab}
                  tabs={[
                    {
                      value: "volume",
                      label: t("component.modal.trend_alerts.tab.volume"),
                      onSelect: setActiveTab,
                    },
                    {
                      value: "newData",
                      label: t("component.modal.trend_alerts.tab.new_data"),
                      onSelect: setActiveTab,
                    },
                  ]}
                />
              </div>
              <TrendAlertItems
                trackerId={trackerId}
                onEdit={handleEditAlert}
                selectedUserId={selectedUserId}
                setSelectedUserId={setSelectedUserId}
                alertGroup={activeTab === "volume" ? "volume" : "newData"}
              />
            </>
          ),
          acceptText: t("component.modal.trend_alerts.button.create_new"),
          acceptAction: () => resetAlertState(),
          cancelText: t("component.modal.trend_alerts.button.cancel"),
          cancelAction: () => {
            closeModal("trend-alert");
          },
        };
      case 2:
        return {
          content:
            activeTab === "volume" ? (
              <AddVolumeTrendAlert
                percentage={percentage}
                onPercentageChange={setPercentage}
                onValueModified={setIsModified}
                initialValue={initialValue}
                currentVolume={currentVolume || null}
                isLoadingCurrentVolume={isLoadingCurrentVolume}
                type={type as "consistent" | "oneTime"}
                onTypeChange={(newType) => setType(newType)}
                targetVolume={targetVolume}
                onTargetVolumeChange={setTargetVolume}
                onAbsoluteNumberChange={setAbsoluteNumber}
              />
            ) : (
              <AddNewDataAlert
                minVolume={minVolume}
                onMinVolumeChange={setMinVolume}
                onValueModified={setIsModified}
                initialMinVolume={initialMinVolume}
              />
            ),
          acceptText: editMode
            ? t("component.modal.trend_alerts.button.save")
            : t("component.modal.trend_alerts.button.create"),
          acceptAction: handleSave,
          cancelText: t("component.modal.trend_alerts.button.cancel"),
          cancelAction: () => {
            setStep(1);
          },
        };
      case 3:
        return {
          content: (
            <TrendAlertConfirmation
              createdAlert={createdAlert}
              editMode={editMode}
            />
          ),
          acceptText: t("component.modal.trend_alerts.button.done"),
          acceptAction: () => {
            setStep(1);
          },
          cancelText: t("component.modal.trend_alerts.button.create_new"),
          cancelAction: () => resetAlertState(),
        };
      default:
        return {};
    }
  };

  if (!formattedTracker) return null;

  const { content, acceptText, acceptAction, cancelText, cancelAction } =
    getModalConfig();

  const handleRequestClose = () => {
    if (step === 2 && isModified) {
      setModal(
        "unsaved-changes",
        <UnsavedChangesModal
          onAccept={() => {
            closeModal("trend-alert");
            closeModal("unsaved-changes");
          }}
          onCancel={() => closeModal("unsaved-changes")}
        />,
      );
    } else {
      closeModal("trend-alert");
    }
  };

  return (
    <ConfirmModal
      id="trend-alert"
      type="success"
      title={t("component.modal.trend_alerts.title")}
      className={styles.modal}
      contentClassName={styles.content}
      acceptButton={{
        text: acceptText,
        onClick: acceptAction,
        disabled:
          isLoading ||
          disableCreateNewDataAlert ||
          (step === 2 && !isModified) ||
          (step === 1 && currentUser.id !== selectedUserId),
      }}
      cancelButton={{
        text: cancelText,
        onClick: cancelAction,
      }}
      onRequestClose={handleRequestClose}
    >
      <SelectedTracker tracker={formattedTracker} />
      {content}
    </ConfirmModal>
  );
};
